import { useCallback, useEffect, useState } from 'react';
import {
  useAddUserFileMutation,
  useChangeUserPasswordMutation,
  useCurrentUserQuery,
  useDeleteUserFileMutation,
  useUpdateUserNameMutation,
} from 'api';
import { notification } from 'components';
import { ErrorMappingValueType } from 'typings';
import { Form, UploadProps } from 'antd';
import { MAX_FILE_SIZE } from 'constants/index';

export const useProfilePage = ({ storageUserIdentifier = 'userId' }: { storageUserIdentifier: string }) => {
  const [form] = Form.useForm();
  const [changePasswordForm] = Form.useForm();
  const [changePassOpened, setChangePassOpened] = useState(false);
  const [editModalOpened, setEditModalOpened] = useState(false);

  const userId = JSON.parse(localStorage.getItem(storageUserIdentifier) || 'null');
  const { data: userDetails = {} } = useCurrentUserQuery({ userId }, { skip: !userId });
  const [addUserFile] = useAddUserFileMutation();
  const [deleteUserFile] = useDeleteUserFileMutation();
  const [userUpdate, { isLoading: updateLoading }] = useUpdateUserNameMutation();
  const [changeUserPassword, { isLoading: changePassLoading }] = useChangeUserPasswordMutation();

  const [notificationApi, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = useCallback(
    ({ type, message }: ErrorMappingValueType) => {
      notificationApi[type]({ message });
    },
    [notificationApi]
  );

  const handleErrors = useCallback(
    (error: any) => {
      if (error?.data?.success === false) {
        Object.values(error.data.data).forEach((message) =>
          openNotificationWithIcon({ type: 'error', message: message as string })
        );
      } else {
        openNotificationWithIcon({ type: 'error', message: 'Something went wrong!' });
      }
    },
    [openNotificationWithIcon]
  );

  const handleAvatarChange: UploadProps['onChange'] = async (info) => {
    const file = info.file;
    if (!file) return;
    if (file?.size && file.size > MAX_FILE_SIZE) {
      openNotificationWithIcon({ type: 'error', message: 'File exceeds the maximum size of 5 MB.' });
      return;
    }
    const formData = new FormData();
    formData.append('adminId', userDetails?.id);
    formData.append('files', file as any, file.name);

    try {
      const response = await addUserFile(formData);
      response?.data?.success
        ? openNotificationWithIcon({ type: 'success', message: 'Profile picture was successfully uploaded.' })
        : handleErrors(response.error);
    } catch (err) {
      handleErrors(err);
    }
  };

  const handleChangePassword = useCallback(async () => {
    const payload = changePasswordForm.getFieldsValue();
    try {
      const response = await changeUserPassword({ ...payload, id: userDetails?.id });
      response?.data?.success
        ? openNotificationWithIcon({ type: 'success', message: 'Password successfully changed!' })
        : handleErrors(response.error);
    } catch (err) {
      handleErrors(err);
    } finally {
      setChangePassOpened(false);
    }
  }, [changePasswordForm, changeUserPassword, userDetails?.id, handleErrors, openNotificationWithIcon]);

  useEffect(() => {
    if (changePassOpened) changePasswordForm.resetFields();
  }, [changePassOpened, changePasswordForm]);

  useEffect(() => {
    form.resetFields();
    if (editModalOpened) {
      form.setFieldsValue({ firstName: userDetails.firstName, lastName: userDetails.lastName });
    }
  }, [editModalOpened, form, userDetails]);

  const handleUserUpdate = useCallback(async () => {
    const payload = form.getFieldsValue();
    try {
      const response = await userUpdate({ ...payload, id: userDetails?.id });
      response?.data?.success
        ? openNotificationWithIcon({ type: 'success', message: 'Profile successfully updated!' })
        : handleErrors(response.error);
    } catch (err) {
      handleErrors(err);
    } finally {
      setEditModalOpened(false);
    }
  }, [form, userUpdate, userDetails?.id, handleErrors, openNotificationWithIcon]);

  const handleAvatarDelete = useCallback(async () => {
    const fileName = userDetails.files?.AVATAR?.split('fileName=')[1];
    if (!fileName) return;

    try {
      const response = await deleteUserFile({ name: fileName, id: userDetails?.id });
      response?.data?.success
        ? openNotificationWithIcon({ type: 'success', message: 'Profile picture was successfully deleted.' })
        : handleErrors(response.error);
    } catch (err) {
      handleErrors(err);
    }
  }, [deleteUserFile, userDetails, handleErrors, openNotificationWithIcon]);

  return {
    userDetails,
    changePassOpened,
    handleAvatarChange,
    setChangePassOpened,
    handleAvatarDelete,
    changePassLoading,
    contextHolder,
    form,
    handleChangePassword,
    updateLoading,
    changePasswordForm,
    handleUserUpdate,
    editModalOpened,
    setEditModalOpened,
  };
};
